import { useCallback, useEffect, useRef, useState } from 'react';
import {
  resolveIdleWarnBeforeMs,
  SESSION_IDLE_ACTIVITY_THROTTLE_MS,
  SESSION_IDLE_TIMEOUT_MS,
  SESSION_IDLE_WARN_BEFORE_MS,
} from '../config/session';

const ACTIVITY_EVENTS = [
  'pointerdown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
] as const;

export type IdleSessionState = {
  /** True once the warn window has opened (until extend or logout). */
  warning: boolean;
  /** Milliseconds until logout while warning; 0 otherwise. */
  remainingMs: number;
  /** Reset the idle timer and dismiss the warning. */
  extend: () => void;
};

/**
 * Tracks idle time, warns before sign-out, and calls `onIdle` only after the
 * full timeout. Any listed DOM activity reactivates the session.
 */
export function useIdleSessionLogout(
  onIdle: () => void,
  enabled = true,
  timeoutMs = SESSION_IDLE_TIMEOUT_MS,
  warnBeforeMs = SESSION_IDLE_WARN_BEFORE_MS,
): IdleSessionState {
  const onIdleRef = useRef(onIdle);
  const [warning, setWarning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(0);
  const deadlineRef = useRef(0);
  const warnIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const logoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const tickIdRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  const clearTimers = useCallback(() => {
    if (warnIdRef.current != null) clearTimeout(warnIdRef.current);
    if (logoutIdRef.current != null) clearTimeout(logoutIdRef.current);
    if (tickIdRef.current != null) clearInterval(tickIdRef.current);
    warnIdRef.current = undefined;
    logoutIdRef.current = undefined;
    tickIdRef.current = undefined;
  }, []);

  const schedule = useCallback(() => {
    clearTimers();
    const timeout = Math.max(2_000, Number(timeoutMs) || SESSION_IDLE_TIMEOUT_MS);
    const warnBefore = resolveIdleWarnBeforeMs(timeout, warnBeforeMs);
    const warnAfter = Math.max(0, timeout - warnBefore);
    deadlineRef.current = Date.now() + timeout;
    setWarning(false);
    setRemainingMs(0);

    warnIdRef.current = setTimeout(() => {
      setWarning(true);
      setRemainingMs(Math.max(0, deadlineRef.current - Date.now()));
      tickIdRef.current = setInterval(() => {
        const left = Math.max(0, deadlineRef.current - Date.now());
        setRemainingMs(left);
      }, 1000);
    }, warnAfter);

    logoutIdRef.current = setTimeout(() => {
      clearTimers();
      setWarning(false);
      setRemainingMs(0);
      onIdleRef.current();
    }, timeout);
  }, [clearTimers, timeoutMs, warnBeforeMs]);

  const extend = useCallback(() => {
    schedule();
  }, [schedule]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      clearTimers();
      setWarning(false);
      setRemainingMs(0);
      return undefined;
    }

    let lastBump = 0;

    const bumpActivity = () => {
      const now = Date.now();
      if (now - lastBump < SESSION_IDLE_ACTIVITY_THROTTLE_MS) return;
      lastBump = now;
      schedule();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') bumpActivity();
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, bumpActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', onVisibilityChange);

    schedule();

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, bumpActivity);
      });
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, schedule, clearTimers]);

  return { warning, remainingMs, extend };
}
